import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  CanMatchFn,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree,
} from '@angular/router';
import { AuthService, UserType } from '../../../services/auth.service';

function denyAccess(authService: AuthService, router: Router): UrlTree {
  authService.clearSession();
  return router.createUrlTree(['/login']);
}

function isRoleAllowed(currentType: UserType | null, expectedType: UserType): boolean {
  return currentType === expectedType;
}

function validateTenantSlug(authService: AuthService, slug: string | null): boolean {
  if (!slug) {
    return false;
  }

  const expectedSlug =
    authService.getTenantSlug() ??
    authService.getTenantSlugFromToken() ??
    window.location.pathname.match(/^\/t\/([^/]+)/)?.[1] ??
    null;

  if (!expectedSlug) {
    return true;
  }

  return expectedSlug === slug;
}

export const platformRoleMatchGuard: CanMatchFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (!authService.hasValidSession()) {
    return denyAccess(authService, router);
  }

  const userType = authService.getUserTypeFromToken() ?? authService.userType();
  if (!isRoleAllowed(userType, 'platform_user')) {
    return denyAccess(authService, router);
  }

  return true;
};

export const tenantRoleMatchGuard: CanMatchFn = (_route: Route, segments: UrlSegment[]) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (!authService.hasValidSession()) {
    return denyAccess(authService, router);
  }

  const userType = authService.getUserTypeFromToken() ?? authService.userType();
  if (!isRoleAllowed(userType, 'tenant_user')) {
    return denyAccess(authService, router);
  }

  const slugFromUrl = segments[1]?.path ?? null;
  if (!validateTenantSlug(authService, slugFromUrl)) {
    return denyAccess(authService, router);
  }

  return true;
};

export const platformRoleActivateGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (!authService.hasValidSession()) {
    return denyAccess(authService, router);
  }

  const userType = authService.getUserTypeFromToken() ?? authService.userType();
  if (!isRoleAllowed(userType, 'platform_user')) {
    return denyAccess(authService, router);
  }

  return true;
};

export const tenantRoleActivateGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot,
) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (!authService.hasValidSession()) {
    return denyAccess(authService, router);
  }

  const userType = authService.getUserTypeFromToken() ?? authService.userType();
  if (!isRoleAllowed(userType, 'tenant_user')) {
    return denyAccess(authService, router);
  }

  const slug = route.paramMap.get('slug');
  if (!validateTenantSlug(authService, slug)) {
    return denyAccess(authService, router);
  }

  return true;
};
